import { ChevronLeft, ChevronRight } from 'lucide-react'

import type { Pagination } from '@/lib/api/types'
import { cn } from '@/lib/utils'
import { getPaginationRange } from '@/shared/lib/get-pagination-range'

export type PaginationLinksProps = {
  pagination: Pagination
  onPageChange: (page: number) => void
  className?: string
}

const navButtonClassName =
  'inline-flex cursor-pointer items-center gap-1 rounded-button-sm px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-surface-muted disabled:pointer-events-none disabled:opacity-40'

const pageButtonClassName =
  'min-w-9 cursor-pointer rounded-button-sm px-3 py-2 text-sm font-semibold transition-colors'

export const PaginationLinks = ({
  pagination,
  onPageChange,
  className,
}: PaginationLinksProps) => {
  if (pagination.total_pages <= 1) {
    return null
  }

  const pages = getPaginationRange(
    pagination.current_page,
    pagination.total_pages
  )

  return (
    <nav
      aria-label="Pagination"
      className={cn('flex flex-wrap items-center justify-center gap-2', className)}
    >
      <button
        type="button"
        className={navButtonClassName}
        aria-label="Go to previous page"
        disabled={pagination.prev_page === null}
        onClick={() => {
          if (pagination.prev_page !== null) {
            onPageChange(pagination.prev_page)
          }
        }}
      >
        <ChevronLeft className="size-4" aria-hidden />
        Previous
      </button>

      <ol className="flex items-center gap-1">
        {pages.map((item, index) =>
          item === 'ellipsis' ? (
            <li
              key={`ellipsis-${index}`}
              aria-hidden
              className="px-2 text-sm text-muted-foreground"
            >
              …
            </li>
          ) : (
            <li key={item}>
              <button
                type="button"
                className={cn(
                  pageButtonClassName,
                  item === pagination.current_page
                    ? 'bg-primary-dark text-white'
                    : 'text-primary hover:bg-surface-muted'
                )}
                aria-label={`Go to page ${item}`}
                aria-current={
                  item === pagination.current_page ? 'page' : undefined
                }
                onClick={() => onPageChange(item)}
              >
                {item}
              </button>
            </li>
          )
        )}
      </ol>

      <button
        type="button"
        className={navButtonClassName}
        aria-label="Go to next page"
        disabled={pagination.next_page === null}
        onClick={() => {
          if (pagination.next_page !== null) {
            onPageChange(pagination.next_page)
          }
        }}
      >
        Next
        <ChevronRight className="size-4" aria-hidden />
      </button>
    </nav>
  )
}
